import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, AlertCircle, XCircle, Clock } from 'lucide-react';

export interface ModuleStatus {
  name: string;
  status: 'working' | 'partial' | 'not_working' | 'in_progress';
  progress: number;
  functionality: string[];
  icon: React.ReactNode;
}

interface ModuleCardProps {
  module: ModuleStatus;
}

const ModuleCard: React.FC<ModuleCardProps> = ({ module }) => {
  const getStatusBadge = (status: ModuleStatus['status']) => {
    switch (status) {
      case 'working':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Работает</Badge>;
      case 'partial':
        return <Badge className="bg-yellow-100 text-yellow-800"><AlertCircle className="w-3 h-3 mr-1" />Частично</Badge>;
      case 'not_working':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Не работает</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-100 text-blue-800"><Clock className="w-3 h-3 mr-1" />В разработке</Badge>;
    }
  };

  const getProgressBarColor = (progress: number) => {
    if (progress >= 90) return 'bg-green-500';
    if (progress >= 75) return 'bg-blue-500';
    if (progress >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <Card className="relative overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center">
            {module.icon}
            <span className="ml-2">{module.name}</span>
          </CardTitle>
          {getStatusBadge(module.status)}
        </div>
        <div className="flex items-center gap-2">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-500 ${getProgressBarColor(module.progress)}`}
              style={{ width: `${module.progress}%` }}
            ></div>
          </div>
          <span className="text-sm font-medium text-gray-600">{module.progress}%</span>
        </div>
      </CardHeader>
      <CardContent>
        <ul className="space-y-1 text-sm">
          {module.functionality.map((func, index) => (
            <li key={index} className="flex items-start">
              <span className="text-gray-600">{func}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

export default ModuleCard;
